import * as THREE from 'three';
import type { SketchEllipseObject } from '../../../mesh/sketch/SketchEllipseObject';
import type { ProjectionResult } from '../model/ProjectionResult';
import type {
	EllipseArcReferenceProjectionState,
	ReferenceDrivenEllipseArcRelationRuntimeRecord,
	SketchEntityReferenceRecord,
} from '../model/SketchEntityReferenceRecord';
import { resolveSourceRefEntityId } from '../model/SketchEntityReferenceRecord';
import type { ExecutionDiagnostic } from '../model/SketchEntityReferenceDiagnostics';
import { BaseReferenceDrivenRelationService } from './BaseReferenceDrivenRelationService';
import { ReferenceDrivenEllipseArcOnCurveConstraint } from './constraints/ReferenceDrivenEllipseArcOnCurveConstraint';
import type {
	ReferenceDrivenEllipseArcRelationBinding,
	ReferenceDrivenEllipseArcRelationAttachResult,
} from '../../../../types/reference/sketch-entity-reference/ReferenceDrivenEllipseArcRelationService';

export type {
	ReferenceDrivenEllipseArcRelationAttachResult,
} from '../../../../types/reference/sketch-entity-reference/ReferenceDrivenEllipseArcRelationService';

/**
 * 生成椭圆弧引用的投影状态。
 *
 * @param targetEllipse 目标椭圆。
 * @returns 椭圆弧投影状态。
 */
function createEllipseArcProjectionState(
	targetEllipse: SketchEllipseObject,
): EllipseArcReferenceProjectionState {
	const definition = targetEllipse.getEllipseDefinition();

	return {
		projectionResultKind: 'ellipse-arc',
		startAngle: definition.startAngle,
		endAngle: definition.endAngle,
		clockwise: definition.clockwise,
	};
}

/**
 * 参考驱动椭圆弧关系服务。
 */
export class ReferenceDrivenEllipseArcRelationService
	extends BaseReferenceDrivenRelationService<
		ReferenceDrivenEllipseArcRelationRuntimeRecord,
		ReferenceDrivenEllipseArcRelationBinding
	> {
	/**
	 * 挂载椭圆弧 on-curve 约束。
	 *
	 * @param input 挂载输入。
	 * @returns 挂载结果。
	 */
	public attach(input: {
		reference: SketchEntityReferenceRecord;
		projectionResult: Extract<ProjectionResult, { projectionResultKind: 'ellipse-arc' }>;
		targetEllipse: SketchEllipseObject;
		runtimeSourceName?: string;
	}): ReferenceDrivenEllipseArcRelationAttachResult {
		this.detach({
			referenceId: input.reference.referenceId,
			targetEllipse: input.targetEllipse,
		});

		const record: ReferenceDrivenEllipseArcRelationRuntimeRecord = {
			referenceId: input.reference.referenceId,
			relationKind: 'on-curve',
			driveMode: 'projection-drives-target',
			sourceRefEntityId: resolveSourceRefEntityId(input.reference.sourceDescriptor),
			targetEntityId: input.targetEllipse.persistentId,
			constraintId: -1,
		};
		const constraint = new ReferenceDrivenEllipseArcOnCurveConstraint({
			referenceId: input.reference.referenceId,
			projectedCenterWorld: new THREE.Vector3(...input.projectionResult.geometryPayload.centerWorld),
			projectedRadiusX: input.projectionResult.geometryPayload.radiusX,
			projectedRadiusY: input.projectionResult.geometryPayload.radiusY,
			projectedRotation: input.projectionResult.geometryPayload.rotation,
			sourceDisplayLabel: input.reference.sourceDisplayLabel,
			runtimeSourceName: input.runtimeSourceName,
		});
		input.targetEllipse.constraints.addRelation(constraint);
		record.constraintId = constraint.id;
		this.setBinding(input.reference.referenceId, {
			constraint,
			record,
			targetEllipse: input.targetEllipse,
		});

		/**
		 * 部分椭圆角度分支从当前候选定义冻结，最终点位与非点参数由统一求解写回。
		 */
		record.projectionState = createEllipseArcProjectionState(input.targetEllipse);
		return {
			record,
			diagnostics: [],
		};
	}

	/**
	 * 拆除椭圆弧关系。
	 *
	 * @param input 拆除输入。
	 * @returns 诊断信息。
	 */
	public detach(input: {
		referenceId: string;
		targetEllipse?: SketchEllipseObject;
	}): ExecutionDiagnostic[] {
		const binding = this.getBinding(input.referenceId);
		if (binding) {
			binding.targetEllipse.constraints.removeRelation(binding.record.constraintId);
			this.deleteBinding(input.referenceId);
			return [];
		}

		if (input.targetEllipse) {
			const constraint = input.targetEllipse.constraints.getRelations().find((candidate) => (
				candidate instanceof ReferenceDrivenEllipseArcOnCurveConstraint
				&& candidate.referenceId === input.referenceId
			));
			if (constraint) {
				input.targetEllipse.constraints.removeRelation(constraint.id);
			}
		}
		return [];
	}
}
